import { useParams, Navigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Stack,
  Button,
  Divider,
  useTheme,
} from '@mui/material';
import { CheckCircle, Phone, Email, ArrowForward } from '@mui/icons-material';
import WhatsappIcon from '../../assets/icons/whatsapp.svg';
import { getInsurerBySlug } from '../../config/insurers.data';

const SUPPORT_WHATSAPP = 'https://api.whatsapp.com/send?phone=5543996823513';

const InsurerPage = () => {
  const { slug } = useParams();
  const theme = useTheme();
  const insurer = getInsurerBySlug(slug);

  // Slug inexistente: volta para a página de dúvidas.
  if (!insurer) return <Navigate to="/duvidas" replace />;

  const { name, logo, tagline, description, stats, coverages, emergency, institutional } =
    insurer;

  return (
    <Box>
      {/* Hero */}
      <Box sx={{ backgroundColor: theme.palette.text.primary, color: 'white' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: '16px',
              px: 3,
              py: 2,
              width: 'fit-content',
              mb: 3,
            }}
          >
            <Box component="img" src={logo} alt={name} sx={{ height: { xs: 36, md: 48 } }} />
          </Box>
          <Typography variant="h1" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, mb: 1 }}>
            Seguro viagem {name}
          </Typography>
          <Typography variant="h4" sx={{ color: theme.palette.grey[100], maxWidth: 640 }}>
            {tagline}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }}>
            <Button
              component={RouterLink}
              to="/"
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              sx={{ borderRadius: '999px', px: 4, textTransform: 'none', fontWeight: 700 }}
            >
              Cotar {name}
            </Button>
            <Button
              component="a"
              href={SUPPORT_WHATSAPP}
              target="_blank"
              rel="noreferrer"
              variant="outlined"
              size="large"
              sx={{
                borderRadius: '999px',
                px: 4,
                textTransform: 'none',
                fontWeight: 700,
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.08)' },
              }}
            >
              Falar com um atendente
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Descrição */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        <Typography variant="h2" sx={{ mb: 2 }}>
          Sobre a {name}
        </Typography>
        <Typography variant="body1" sx={{ color: theme.palette.text.secondary, maxWidth: 820, fontSize: '1.05rem' }}>
          {description}
        </Typography>

        {/* Números */}
        <Grid container spacing={3} sx={{ mt: 3 }}>
          {stats.map((stat) => (
            <Grid size={{ xs: 6, md: 3 }} key={stat.label}>
              <Box
                sx={{
                  backgroundColor: theme.palette.grey[50],
                  borderRadius: '16px',
                  p: 3,
                  height: '100%',
                }}
              >
                <Typography variant="h3" sx={{ color: theme.palette.primary.main }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2">{stat.label}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Coberturas */}
      <Box sx={{ backgroundColor: theme.palette.grey[50] }}>
        <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
          <Typography variant="h2" sx={{ mb: 1 }}>
            Coberturas
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            As coberturas exatas variam conforme o plano escolhido. Entre as principais estão:
          </Typography>
          <Grid container spacing={2}>
            {coverages.map((coverage) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={coverage}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CheckCircle sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="body1">{coverage}</Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contatos de emergência */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        <Typography variant="h2" sx={{ mb: 1 }}>
          Contatos de emergência 24h
        </Typography>
        <Typography variant="body2" sx={{ mb: 3 }}>
          Em caso de emergência durante a viagem, acione a {name} diretamente pelos canais abaixo:
        </Typography>
        <Grid container spacing={3}>
          {emergency.map((contact) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={contact.label}>
              <Box
                component={contact.href ? 'a' : 'div'}
                href={contact.href}
                target={contact.href && contact.href.startsWith('http') ? '_blank' : undefined}
                rel="noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  textDecoration: 'none',
                  color: 'inherit',
                  border: `1px solid ${theme.palette.grey[100]}`,
                  borderRadius: '16px',
                  p: 3,
                  height: '100%',
                  transition: 'border-color 0.2s ease',
                  '&:hover': { borderColor: theme.palette.primary.main },
                }}
              >
                {contact.whatsapp ? (
                  <Box component="img" src={WhatsappIcon} alt="WhatsApp" sx={{ width: 28, height: 28 }} />
                ) : contact.href && contact.href.startsWith('mailto') ? (
                  <Email sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
                ) : (
                  <Phone sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
                )}
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.25 }}>
                    {contact.label}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                    {contact.value}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Dados institucionais */}
      <Box sx={{ backgroundColor: theme.palette.grey[50] }}>
        <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
          <Typography variant="h3" sx={{ mb: 3 }}>
            Dados institucionais
          </Typography>
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: '16px',
              p: { xs: 3, md: 4 },
            }}
          >
            <Stack divider={<Divider />} spacing={2}>
              {Object.entries(institutional).map(([key, value]) => (
                <Stack
                  key={key}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 0.25, sm: 2 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 180 }}>
                    {key}
                  </Typography>
                  <Typography variant="body1">{value}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* CTA final */}
      <Box sx={{ backgroundColor: theme.palette.primary.main, color: 'white' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 }, textAlign: 'center' }}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            Pronto para viajar tranquilo?
          </Typography>
          <Typography variant="h4" sx={{ mb: 4, opacity: 0.9 }}>
            Compare as opções da {name} e das outras seguradoras em poucos minutos.
          </Typography>
          <Button
            component={RouterLink}
            to="/"
            variant="contained"
            size="large"
            endIcon={<ArrowForward />}
            sx={{
              borderRadius: '999px',
              px: 5,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 700,
              backgroundColor: 'white',
              color: theme.palette.primary.main,
              '&:hover': { backgroundColor: theme.palette.grey[50] },
            }}
          >
            Cotar seguro viagem
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default InsurerPage;
